import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';

import {
  CREATE_BULK_UPLOAD,
  REGISTER_BULK_UPLOAD,
} from 'mutations/bulkOperations';

import { MAX_ROWS, MAX_SIZE_BYTES, csvSpecForActions } from './constants';

const IDLE = { loading: false, data: undefined, error: undefined };

const KB = 1024;
const MB = KB * KB;

/** Matches `BulkUploadCsv.rowOf` in fraud-rules-service. */
const cell = (value = '') => value.trim().replace(/^"+|"+$/g, '');

/** Adaptive: a per-environment limit under half a megabyte would round to "0 MB". */
const asSize = (bytes) => {
  if (bytes >= MB) {
    return `${+(bytes / MB).toFixed(1)} MB`;
  }
  if (bytes >= KB) {
    return `${Math.round(bytes / KB)} KB`;
  }
  return bytes === 1 ? '1 byte' : `${bytes} bytes`;
};

const tooLarge = (bytes) =>
  `This file is larger than the ${asSize(bytes)} limit.`;

/**
 * Every error found in a file, not just the first: an agent fixing a file one
 * line per attempt would be here all afternoon. `message` alone still reads
 * whole when there is only one.
 */
export class CsvValidationError extends Error {
  constructor(errors) {
    super(
      errors.length === 1
        ? errors[0]
        : `This file has ${errors.length.toLocaleString()} errors.`,
    );
    this.name = 'CsvValidationError';
    this.errors = errors;
  }
}

/**
 * Mirrors `BulkUploadCsv.read` in fraud-rules-service, down to the naive
 * `split(',')`: neither side parses quoted commas, so both read the same file the
 * same way and this refuses exactly what the service would. Rejecting here costs
 * a message rather than a signed URL, an upload and an abandoned `bulk_upload`.
 *
 * `csvSpec` comes from `csvSpecForActions` and says which columns are required
 * and what each of their values must look like.
 */
export const validateCsv = (text, csvSpec) => {
  // The header is the literal first line, blank or not, because that is the line
  // the service reads. Skipping to the first non-blank one here would accept a
  // file it rejects, which is the whole failure this function exists to prevent.
  const [headerLine = '', ...dataLines] = text.split(/\r?\n/);

  // Excel's "CSV UTF-8" export prepends a BOM, and it is not whitespace, so it
  // has to come off explicitly or the first column never matches.
  const headerColumns = headerLine
    .replace(/^\uFEFF/, '')
    .split(',')
    .map(cell);

  // Matched case-insensitively but kept in the file's own casing, so the error
  // below names the columns back the way the agent wrote them.
  const requiredColumns = csvSpec.requiredColumns.map((requiredColumn) => ({
    ...requiredColumn,
    index: headerColumns.findIndex(
      (headerColumn) => headerColumn.toLowerCase() === requiredColumn.name,
    ),
  }));

  const missingColumns = requiredColumns.filter(({ index }) => index === -1);
  if (missingColumns.length) {
    throw new CsvValidationError(
      missingColumns.map(
        ({ name }) =>
          `This file has no ${name} column. Its columns are: ${headerColumns.join(', ')}.`,
      ),
    );
  }

  // Numbered before the blanks are dropped, so a line number is one the agent
  // can count to in their own file. The service numbers them the same way.
  const rows = dataLines
    .map((line, index) => ({ lineNumber: index + 2, line }))
    .filter(({ line }) => line.trim())
    .map(({ lineNumber, line }) => ({ lineNumber, values: line.split(',') }));

  if (!rows.length) {
    throw new CsvValidationError(['This file has a header but no rows.']);
  }

  if (rows.length > MAX_ROWS) {
    throw new CsvValidationError([
      `This file has ${rows.length.toLocaleString()} rows, which is over the ${MAX_ROWS.toLocaleString()} row limit.`,
    ]);
  }

  // One bad value makes the service refuse the whole file, so it is worth the
  // scan to say which lines rather than leaving the agent to find them.
  const [idColumn] = requiredColumns;
  const firstLineNumberById = new Map();
  const errors = [];

  rows.forEach(({ lineNumber, values }) => {
    requiredColumns.forEach(({ name, index, valuePattern, formatHint }) => {
      const cellValue = cell(values[index]);
      if (!cellValue) {
        errors.push(`Line ${lineNumber} has no ${name}.`);
      } else if (valuePattern && !valuePattern.test(cellValue)) {
        // The value is left out: it may be personal, and the agent has the line.
        errors.push(
          `Line ${lineNumber} has a ${name} that is not ${formatHint}.`,
        );
      }
    });

    const rowId = cell(values[idColumn.index]);
    if (!rowId) {
      return;
    }
    if (!firstLineNumberById.has(rowId)) {
      firstLineNumberById.set(rowId, lineNumber);
    } else if (csvSpec.requireUniqueIds) {
      errors.push(
        `Line ${lineNumber} repeats the ${idColumn.name} on line ${firstLineNumberById.get(rowId)}.`,
      );
    }
  });

  if (errors.length) {
    throw new CsvValidationError(errors);
  }

  return {
    // The one count the service does not return: it reports rows, and the same
    // account may legitimately appear on several of them.
    uniqueIdCount: firstLineNumberById.size,
    recordType: csvSpec.recordType,
  };
};

/** Asks for a signed URL, PUTs the file straight to storage, then registers it. */
const useUploadBulkFile = () => {
  const client = useApolloClient();
  const [state, setState] = useState(IDLE);

  const upload = useCallback(
    async (file, { sourceType, actions, label }) => {
      setState({ ...IDLE, loading: true });

      try {
        // Before reading, not after: the limit that decides comes back from
        // createBulkUpload, and waiting for it would mean pulling a file of any
        // size into memory first.
        if (file.size > MAX_SIZE_BYTES) {
          throw new Error(tooLarge(MAX_SIZE_BYTES));
        }

        const fileSummary = validateCsv(
          await file.text(),
          csvSpecForActions(actions),
        );

        const { data: urlData } = await client.mutate({
          mutation: CREATE_BULK_UPLOAD,
          variables: { sourceType, actions, filename: file.name, label },
        });
        const target = urlData.createBulkUpload;

        // The authoritative limit, which is per-environment and need not match
        // the one above. A signed URL cannot carry a size limit of its own.
        if (file.size > target.maxSizeBytes) {
          throw new Error(tooLarge(target.maxSizeBytes));
        }

        // No auth headers: the signature is the authority, and anything extra
        // would not match what was signed.
        const response = await fetch(target.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': target.contentType },
          body: file,
        });

        if (!response.ok) {
          throw new Error('The file could not be uploaded. Please try again.');
        }

        const { data: registerData } = await client.mutate({
          mutation: REGISTER_BULK_UPLOAD,
          variables: { uploadToken: target.uploadToken, sourceType, label },
        });

        // Registration last, so its rowCount and hashes — read back off the
        // stored object — win over anything worked out here from the file.
        const data = { ...fileSummary, ...registerData.registerBulkUpload };

        setState({ loading: false, data, error: undefined });
        return data;
      } catch (error) {
        setState({ loading: false, data: undefined, error });
        return undefined;
      }
    },
    [client],
  );

  const reset = useCallback(() => setState(IDLE), []);

  return [upload, { ...state, reset }];
};

export default useUploadBulkFile;
